/**
 * @component ContentAdminPanel
 * @description 레지스트리에 등록된 공개 페이지 문구를 편집하고 전체 교체 저장하는 관리자 패널입니다.
 * @usage AdminPage의 '인사말' 탭(/admin)에서 렌더링됩니다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import AdminToolbar from '../../components/admin/AdminToolbar';
import AdminRowButton from '../../components/admin/AdminRowButton';
import { useEditableList } from '../../hooks/useEditableList';
import { listContentKeys, getContentEntry, getDefaultText, getDefaultItems } from '../../content/registry';
import { useSeedReset } from '../../hooks/useSeedReset';
import ModalPopup from '../../components/common/ModalPopup';
import * as adminApi from '../../services/adminApi';
import { AdminAuthError } from '../../services/adminApi';
import { MAX_LIST_ITEMS, stripLineBreaks, buildContentValues, mergeContentValues } from '../../services/contentPayload';

// [기능] 레지스트리에서 한 줄 및 긴 텍스트 편집 키를 선택합니다.
const TEXT_KEYS = listContentKeys().filter((key) => {
  const type = getContentEntry(key).type;
  return type === 'text' || type === 'longtext';
});
// [비즈니스] 현재 목록은 하나뿐이며 다중 목록 지원 전까지 추가 목록은 외부 키로 보존합니다.
const PARAGRAPH_KEY = listContentKeys().find((key) => getContentEntry(key).type === 'list') ?? null;
// [비즈니스] 화면에 그리는 키만 소유해야 저장 시 보이지 않는 다른 문구를 지우지 않습니다.
const OWNED_KEYS = listContentKeys().filter((key) => TEXT_KEYS.includes(key) || key === PARAGRAPH_KEY);

// [기능] 새 문단의 빈 초안을 만듭니다.
function makeEmptyRow() {
  return { text: '' };
}

// [기능] Enter 개행을 막되 한글 조합 확정은 허용합니다.
function blockEnter(event) {
  const native = event.nativeEvent;
  // [기술] 브라우저에 따라 조합 상태를 isComposing 또는 keyCode 229로 알립니다.
  if (native?.isComposing === true || native?.keyCode === 229) return;
  if (event.key === 'Enter') event.preventDefault();
}

// [기능] 대량 삭제 방어 오류에 복구 안내를 붙이고 그 외 오류는 그대로 표시합니다.
function toSaveErrorMessage(message) {
  // [비즈니스] 대량 삭제는 병합 누락 신호이므로 서버 한도를 높이지 않고 재편집을 안내합니다.
  if (typeof message === 'string' && message.includes('한 번에 지울 수 있는 문구는')) {
    return `${message} 저장을 취소했습니다. 화면을 새로고침한 뒤 다시 편집해 주세요.`;
  }
  return message;
}

// [기능] ISO 시각을 로컬 YYYY-MM-DD HH:mm으로 바꾸며 해석 불가 시 빈 문자열을 반환합니다.
// [비즈니스] 저장한 사람의 벽시계 시각을 보여야 하므로 UTC 문자열을 잘라 쓰지 않습니다.
function formatSavedAt(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

// [기능] 문구 초안, 문단 편집, 최신 서버 맵과의 병합 저장을 제공합니다.
export default function ContentAdminPanel({ onUnauthorized }) {
  const { rows, setRowsFromServer, updateRow, toggleDelete, addRow, removeRow, moveRow, moveRowTo,
    resetDraft, buildPayload, isDirty: isListDirty } = useEditableList(makeEmptyRow);
  const [texts, setTexts] = useState({});
  const textSnapshotRef = useRef('{}');
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // [기술] 끌고 있는 행과 커서가 올라가 있는 행을 uid 로 기억합니다. 인덱스는 이동 중에 바뀌므로 쓰지 않습니다.
  const [dragUid, setDragUid] = useState(null);
  const [dropUid, setDropUid] = useState(null);

  const isDirty = isListDirty || JSON.stringify(texts) !== textSnapshotRef.current;

  // [기능] 조회·저장 응답으로 초안과 변경 감지 스냅샷을 갱신합니다.
  // [비즈니스] 없거나 형식이 맞지 않는 오버라이드는 공개 페이지처럼 기본값으로 보여 줍니다.
  const applyRecord = useCallback((record) => {
    const candidate = record?.values;
    const values = candidate && typeof candidate === 'object'
      && (Object.getPrototypeOf(candidate) === Object.prototype || Object.getPrototypeOf(candidate) === null)
      ? candidate : {};
    const nextTexts = {};
    for (const key of TEXT_KEYS) {
      nextTexts[key] = typeof values[key]?.v === 'string' ? values[key].v : getDefaultText(key);
    }
    if (PARAGRAPH_KEY) {
      const entry = values[PARAGRAPH_KEY];
      const items = Array.isArray(entry?.items) && entry.items.every((item) => typeof item === 'string')
        ? entry.items : getDefaultItems(PARAGRAPH_KEY);
      setRowsFromServer(items.map((text) => ({ text })));
    }
    textSnapshotRef.current = JSON.stringify(nextTexts);
    setTexts(nextTexts);
    setSavedAt(typeof record?.updatedAt === 'string' ? record.updatedAt : null);
  }, [setRowsFromServer]);

  // [기능] 마운트 시 문구를 조회하며 탭을 떠난 뒤 도착한 응답은 적용하지 않습니다.
  useEffect(() => {
    let isActive = true;
    adminApi.fetchContent()
      .then((record) => { if (isActive) applyRecord(record); })
      .catch((error) => { if (isActive) setErrorMessage(error.message); })
      .finally(() => { if (isActive) setLoading(false); });
    return () => { isActive = false; };
  }, [applyRecord]);

  // [기능] 초안을 최신 서버 맵과 정화 병합해 전체 교체 저장합니다.
  // [비즈니스] 이 패널의 키만 보내면 다른 페이지의 문구가 지워지므로 저장 직전에 다시 조회합니다.
  const handleSave = async () => {
    setErrorMessage('');
    setIsSaving(true);
    try {
      const own = buildContentValues({
        texts,
        lists: PARAGRAPH_KEY ? { [PARAGRAPH_KEY]: buildPayload((row) => row.text) } : {},
      });
      let latest;
      try {
        latest = await adminApi.fetchContent();
      } catch (error) {
        if (error instanceof AdminAuthError) throw error;
        // [비즈니스] 최신 맵을 모른 채 전체 교체하지 않습니다.
        setErrorMessage('현재 저장된 문구를 확인하지 못해 저장을 중단했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }
      const { values, droppedKeys } = mergeContentValues(latest?.values, own, OWNED_KEYS);
      if (droppedKeys.length > 0) {
        // [비즈니스] 표현 불가 항목을 버린 채 저장하면 영구 소실되므로 중단합니다.
        setErrorMessage('저장된 문구 중 형식을 알 수 없는 항목이 있어 저장을 중단했습니다. 데이터 파일을 확인해 주세요.');
        return;
      }
      applyRecord(await adminApi.saveContent(values));
    } catch (error) {
      if (error instanceof AdminAuthError) { onUnauthorized(); return; }
      setErrorMessage(toSaveErrorMessage(error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // [기능] 저장된 스냅샷으로 텍스트와 문단을 되돌리고 오류 안내를 지웁니다.
  const handleReset = () => {
    resetDraft();
    setTexts(JSON.parse(textSnapshotRef.current));
    setErrorMessage('');
  };

  // [기술] 드래그 도중 저장 응답이 목록을 새 uid 로 바꾸면, 브라우저의 dragend 가 떨어져 나간 노드에서 나서
  //        React 에 도달하지 않습니다. 원시 dragUid 대신 '지금 목록에 그 행이 있는가' 로 판정해야
  //        is-sorting(입력칸 pointer-events: none)이 영구히 남지 않습니다.
  const dragIndex = dragUid === null ? -1 : rows.findIndex((row) => row._uid === dragUid);
  const isSorting = dragIndex >= 0;

  // [기능] 손잡이에서 행 드래그를 시작하고 행 전체를 드래그 이미지로 지정합니다.
  const handleGripDragStart = (event, row) => {
    // [비즈니스] ↑↓ 버튼이 비활성인 조건과 맞춥니다. 삭제 표시 행은 움직이지 않고, 문단 1개는 옮길 곳이 없습니다.
    if (row._deleted || rows.length < 2) { event.preventDefault(); return; }
    const dataTransfer = event.dataTransfer;
    dataTransfer.effectAllowed = 'move';
    // [기술] Firefox 는 dragstart 에서 setData 를 호출하지 않으면 드래그를 시작하지 않습니다.
    //        값은 빈 문자열로 둡니다. 목록 밖 입력칸에 떨어뜨려도 글자가 끼어들지 않게 하기 위해서입니다.
    dataTransfer.setData('text/plain', '');
    const rowElement = event.currentTarget.closest('li');
    const rect = rowElement.getBoundingClientRect();
    // [기술] 손잡이만 끌리지만 사용자에게는 행 전체가 따라오는 것으로 보여 줍니다.
    dataTransfer.setDragImage(rowElement, event.clientX - rect.left, event.clientY - rect.top);
    setDragUid(row._uid);
    setDropUid(null);
  };

  // [기능] 내부 행 드래그만 수락하고 놓을 행을 표시합니다.
  const handleRowDragOver = (event, row) => {
    // [비즈니스] 이 목록의 행 드래그가 아니면(입력칸 안 글자를 끌거나 바깥 파일을 끄는 경우) 브라우저 기본 동작을 그대로 둡니다.
    if (!isSorting) return;
    // [기술] dragover 에서 preventDefault 해야 이 요소에 drop 이벤트가 발생합니다.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropUid(row._uid === dragUid ? null : row._uid);
  };

  // [기능] 놓은 행의 인덱스로 이동하고 드래그 표시를 해제합니다.
  const handleRowDrop = (event, index) => {
    if (!isSorting) return;
    // [기술] 브라우저 기본 드롭(입력칸 텍스트 삽입, Firefox 의 링크 이동)을 막습니다.
    event.preventDefault();
    // [비즈니스] 놓은 행의 자리를 끌어온 행이 차지합니다. 같은 행에 놓으면 moveItem 이 원본을 돌려주므로 변경으로 잡히지 않습니다.
    moveRowTo(dragUid, index);
    setDragUid(null);
    setDropUid(null);
  };

  // [기능] 드롭 성공과 취소 및 목록 밖 드롭의 상태를 해제합니다.
  const handleGripDragEnd = () => {
    setDragUid(null);
    setDropUid(null);
  };

  // [기능] 커서가 목록 밖으로 나가면 드롭 위치 표시를 지웁니다.
  const handleListDragLeave = (event) => {
    if (isSorting && !event.currentTarget.contains(event.relatedTarget)) setDropUid(null);
  };

  const { isResetting, requestReset, dialogProps } = useSeedReset({
    target: 'content',
    isDirty: isDirty,
    // [기능] 초기화 응답으로 편집 상태를 교체하고 기존 오류를 지웁니다.
    onApplied: (data) => {
      setErrorMessage('');
      applyRecord(data);
    },
    onError: setErrorMessage,
    onUnauthorized,
  });

  if (loading) return <p className="admin-loading">불러오는 중...</p>;
  const savedLabel = savedAt ? formatSavedAt(savedAt) : '';

  return (
    <section className="admin-panel">
      <AdminToolbar
        title="인사말 관리"
        hint={`공개 페이지의 문구를 직접 수정합니다. 저장한 내용은 공개 페이지를 새로고침하면 반영됩니다.${savedLabel ? ` 마지막 저장: ${savedLabel}` : ''}`}
        errorMessage={errorMessage}
        isSaving={isSaving}
        isDirty={isDirty}
        onSave={handleSave}
        onReset={handleReset}
        onSeedReset={requestReset}
        isResetting={isResetting}
      />
      {/* [기능] 레지스트리 순서와 동일하게 소유 키의 입력 블록을 표시합니다. */}
      {OWNED_KEYS.map((key) => {
        const entry = getContentEntry(key);
        if (key !== PARAGRAPH_KEY) {
          // [기능] 붙여넣기를 포함한 텍스트 입력의 개행을 정화합니다.
          const onChange = (event) => setTexts({ ...texts, [key]: stripLineBreaks(event.target.value) });
          return (
            <div key={key} className="admin-field admin-content-field">
              <span className="admin-field-label">{entry.label}</span>
              {entry.type === 'longtext'
                ? <textarea className="admin-input admin-textarea" rows={3} value={texts[key] ?? ''}
                    onChange={onChange} onKeyDown={blockEnter} aria-label={entry.label} />
                : <input type="text" className="admin-input" value={texts[key] ?? ''}
                    onChange={onChange} onKeyDown={blockEnter} aria-label={entry.label} />}
            </div>
          );
        }
        return (
          <div key={key} className="admin-content-list">
            <h3 className="admin-content-subtitle">{entry.label}</h3>
            {/* [비즈니스] 개행 차단 이유와 문단을 나누는 방법을 미리 안내합니다. */}
            <p className="admin-toolbar-hint">줄바꿈은 저장되지 않습니다. 문단을 나누려면 아래 '{entry.itemLabel} 추가'를 사용하세요.</p>
            <ul className={`admin-rows${isSorting ? ' is-sorting' : ''}`} onDragLeave={handleListDragLeave}>
              {rows.map((row, index) => {
                // [비즈니스] ↑↓ 비활성 규칙과 같게, 삭제 표시 행이나 문단이 1개일 때는 끌 수 없습니다.
                const canDrag = !row._deleted && rows.length > 1;
                const className = [
                  'admin-row admin-row-paragraph',
                  row._deleted && 'is-deleted',
                  row._uid === dragUid && isSorting && 'is-dragging',
                  // [비즈니스] 위로 끌면 대상 행 위에, 아래로 끌면 대상 행 아래에 선을 그어 놓일 자리를 보여 줍니다.
                  row._uid === dropUid && isSorting && (index < dragIndex ? 'is-drop-before' : 'is-drop-after'),
                ].filter(Boolean).join(' ');
                return (
                  <li key={row._uid} className={className}
                    onDragOver={(event) => handleRowDragOver(event, row)}
                    onDrop={(event) => handleRowDrop(event, index)}>
                    <div className="admin-row-paragraph-handle">
                      <span className="admin-row-index">{index + 1}</span>
                      {/* [비즈니스] 마우스 전용 보조 수단입니다. 키보드·화면낭독기·터치 사용자는 ↑↓ 버튼으로 옮깁니다. */}
                      <span className="admin-row-grip" draggable={canDrag} aria-hidden="true"
                        title={canDrag ? '끌어서 순서 바꾸기' : undefined}
                        onDragStart={(event) => handleGripDragStart(event, row)}
                        onDragEnd={handleGripDragEnd}>{'\u22EE\u22EE'}</span>
                    </div>
                  <textarea className="admin-input admin-textarea admin-paragraph-input" rows={3}
                    value={row.text} onChange={(event) => updateRow(row._uid, 'text', stripLineBreaks(event.target.value))}
                    onKeyDown={blockEnter} disabled={row._deleted} aria-label={`${entry.itemLabel} ${index + 1}`} />
                  <div className="admin-row-paragraph-actions">
                    <AdminRowButton variant="up" onClick={() => moveRow(row._uid, -1)} disabled={row._deleted || index === 0} />
                    <AdminRowButton variant="down" onClick={() => moveRow(row._uid, 1)} disabled={row._deleted || index === rows.length - 1} />
                    {/* [비즈니스] 새 문단은 저장 전이므로 즉시 제거하고 기존 문단은 삭제 표시만 합니다. */}
                    {row._isNew
                      ? <AdminRowButton variant="delete" title={`${entry.itemLabel} 제거`} onClick={() => removeRow(row._uid)} />
                      : <AdminRowButton variant={row._deleted ? 'restore' : 'delete'} onClick={() => toggleDelete(row._uid)} />}
                  </div>
                  </li>
                );
              })}
            </ul>
            {/* [비즈니스] 서버의 40개 제한에 도달하면 문단 추가를 막습니다. */}
            <button type="button" className="admin-btn admin-btn-ghost admin-add-btn" onClick={addRow}
              disabled={rows.filter((row) => !row._deleted).length >= MAX_LIST_ITEMS}>
              + {entry.itemLabel} 추가
            </button>
          </div>
        );
      })}
      <ModalPopup {...dialogProps} />
    </section>
  );
}
